import {
  Box,
  Button,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid'
import { useMemo, useState } from 'react'
import { useStoryous } from '~/features/storyous/hooks/useStoryous'
import { getStoryousWarnings, WarningId } from '~/features/storyous/getStoryousWarnings'
import { useGetInventory } from '../hooks/useGetInventory'
import { EmptyStateRadar } from '~/ui/components/EmptyState/EmptyStateRadar'
import { CustomDataGrid } from '~/ui/components/CustomDataGrid'
import { useDataGridProps } from '~/ui/hooks/useDataGridProps'
import { SelectionPanel, SelectionPanelAction } from '~/ui/components/SelectionPanel'
import { usePatchModels } from '~/features/models/models-list/hooks/usePatchModels'
import * as R from 'remeda'

type WarningRow = ReturnType<typeof getStoryousWarnings>[number]

export const StoryousList = () => {
  const {
    data: storyousModels,
    isLoading: isStoryousLoading,
    menuData,
    stockData,
  } = useStoryous({
    merchants: ['wollem'],
  })

  const columns = useMemo<GridColDef<WarningRow>[]>(() => {
    return [
      {
        field: 'name',
        headerName: 'Stock code',
        width: 130,
      },
      {
        field: 'amount',
        headerName: 'Storyous amount',
        width: 140,
      },
      {
        field: 'store',
        headerName: 'Storyous Store',
        width: 140,
      },
      {
        field: 'price',
        headerName: 'Storyous Price',
        width: 120,
      },
      {
        field: 'warning',
        headerName: 'Warning',
        flex: 1,
      },
    ]
  }, [])

  const { data: inventory, isLoading: isInventoryLoading } = useGetInventory({
    filters: [{ column: 'status', operator: 'eq', value: 'available' }],
  })
  const isLoading = isInventoryLoading || isStoryousLoading

  const allWarnings = useMemo(() => {
    if (isLoading) return []
    return getStoryousWarnings({ storyousModels, inventory })
  }, [isLoading, inventory, storyousModels])

  const [warningFilter, setWarningFilter] = useState<WarningId | 'all'>('all')

  const warnings = useMemo(() => {
    if (warningFilter === 'all') return allWarnings
    return allWarnings.filter((warning) => warning.warningId === warningFilter)
  }, [allWarnings, warningFilter])

  const { selectedRows, onRowSelectionModelChange, selectionModel, reset } =
    useDataGridProps<WarningRow>({
      rows: warnings,
    })

  const handleWarningFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilter: WarningId | 'all' | null
  ) => {
    if (newFilter !== null) {
      setWarningFilter(newFilter)
      reset()
    }
  }

  const { mutate: patchModels, isPending: isPatchingPrices } = usePatchModels()

  const actions: SelectionPanelAction[] = useMemo(
    () => [
      {
        key: 'match-storyous-prices',
        Component: () => {
          const disabled = isPatchingPrices || isLoading

          return (
            <Button
              onClick={() => {
                // Create inventory map to get model IDs
                const inventoryMap = R.mapToObj(inventory ?? [], (inv) => [
                  inv.models_v2.stock_code,
                  inv,
                ])

                // Map selected rows with price issues to price updates
                const priceUpdates = R.pipe(
                  selectedRows,
                  R.map((row) => {
                    const modelInventory = inventoryMap[row.name]
                    if (!modelInventory) return null

                    // Get the model ID from inventory
                    const modelId = modelInventory.models_v2.id

                    return {
                      id: modelId,
                      stock_code: row.name,
                      price: row.price, // Storyous price
                    }
                  }),
                  R.filter(R.isNonNull),
                  R.uniqueBy((update) => update.id) // Ensure unique model IDs
                )

                if (priceUpdates.length > 0) {
                  patchModels(priceUpdates, { onSuccess: reset })
                }
              }}
              disabled={disabled}
              endIcon={isPatchingPrices ? <CircularProgress size={16} /> : null}
            >
              Match prices
            </Button>
          )
        },
      },
    ],
    [isPatchingPrices, isLoading, inventory, selectedRows, patchModels, reset]
  )

  return (
    <Stack sx={{ flex: 1 }} spacing={1}>
      <Typography variant="h6">Storyous warnings</Typography>
      <Stack sx={{ justifyContent: 'center', alignItems: 'center' }}>
        <ToggleButtonGroup
          value={warningFilter}
          onChange={handleWarningFilterChange}
          exclusive
          aria-label="warning filters"
          size="small"
          sx={{ whiteSpace: 'nowrap' }}
        >
          <ToggleButton value="all" aria-label="all">
            All
          </ToggleButton>
          <ToggleButton value="price-mismatch" aria-label="price mismatch">
            Price Mismatch
          </ToggleButton>
          <ToggleButton value="quantity-mismatch" aria-label="quantity mismatch">
            Quantity Mismatch
          </ToggleButton>
          <ToggleButton value="path-mismatch" aria-label="path mismatch">
            Path Mismatch
          </ToggleButton>
          <ToggleButton value="missing-item" aria-label="missing item">
            Missing Item
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Box sx={{ position: 'relative', flexGrow: 1, height: 500 }}>
        <CustomDataGrid
          rows={warnings}
          columns={columns}
          loading={isLoading}
          checkboxSelection
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={onRowSelectionModelChange}
          isRowSelectable={(params: GridRowParams<WarningRow>) =>
            params.row.warningId === 'price-mismatch'
          }
          slots={{
            noRowsOverlay: () => (
              <EmptyStateRadar
                title="No warnings"
                description="No warnings found"
                featureIconSize="small"
              />
            ),
          }}
        >
          <SelectionPanel rows={selectedRows} actions={actions} onClose={reset} />
        </CustomDataGrid>
      </Box>

      <Stack sx={{ height: 500 }} direction="row" spacing={2} useFlexGap>
        <Stack sx={{ flexGrow: 1 }}>
          <Typography variant="h6">Menu data</Typography>
          <DataGrid
            columns={[
              { field: 'name', headerName: 'Name', flex: 1 },
              { field: 'price', headerName: 'Price', flex: 1 },
              { field: 'store', headerName: 'Store', flex: 1 },
            ]}
            rows={menuData.map((m, index) => ({ ...m, id: `${m.name}-${index}` }))}
          />
        </Stack>
        <Stack sx={{ flexGrow: 1 }}>
          <Typography variant="h6">Stock data</Typography>
          <DataGrid
            columns={[
              { field: 'name', headerName: 'Name', flex: 1 },
              { field: 'amount', headerName: 'Amount', flex: 1 },
              { field: 'store', headerName: 'Store', flex: 1 },
            ]}
            rows={stockData.map((m, index) => ({ ...m, id: `${m.name}-${index}` }))}
          />
        </Stack>
      </Stack>
    </Stack>
  )
}
